import { useState } from "react";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { CheckCircle, Star, ArrowLeft } from "lucide-react";

interface Scenario {
  question: string;
  options: { text: string; score: number; feedback: string }[];
}

const SCENARIOS: Scenario[] = [
  {
    question: "صديقك حزين ويبكي... ماذا تفعل؟",
    options: [
      { text: "أذهب إليه وأسأله ماذا حصل 💛", score: 10, feedback: "رائع! أنت صديق حنون ومهتم!" },
      { text: "أحضر له لعبة ليلعب بها 🎁", score: 8, feedback: "جميل! المشاركة تُسعد الآخرين!" },
      { text: "أتركه لوحده 😶", score: 2, feedback: "الأفضل أن نكون بجانب أصدقائنا عندما يحتاجوننا" },
    ],
  },
  {
    question: "زميلك أخذ لعبتك بدون إذنك... ماذا تفعل؟",
    options: [
      { text: "أقول له بهدوء: من فضلك أعدها لي 🙏", score: 10, feedback: "ممتاز! التحدث بهدوء هو أفضل طريقة!" },
      { text: "أخبر المعلمة 👩‍🏫", score: 7, feedback: "جيد! طلب المساعدة من الكبار فكرة ذكية!" },
      { text: "آخذ لعبته أنا أيضاً 😤", score: 1, feedback: "هذا ليس عادلاً... الأفضل نتحدث بدل الرد بالمثل" },
    ],
  },
  {
    question: "المعلمة طلبت منكم العمل كفريق... ماذا تفعل؟",
    options: [
      { text: "أشارك أفكاري وأستمع للآخرين 🤝", score: 10, feedback: "رائع! العمل الجماعي يعني المشاركة والاستماع!" },
      { text: "أحاول أعمل كل شيء لوحدي 💪", score: 3, feedback: "الفريق أقوى عندما نعمل معاً!" },
      { text: "أترك الآخرين يعملون وأشاهد 👀", score: 2, feedback: "جرّب تشارك! ستستمتع أكثر مع الفريق!" },
    ],
  },
  {
    question: "طفل جديد جاء للفصل ولا يعرف أحداً... ماذا تفعل؟",
    options: [
      { text: "أرحب به وأدعوه للعب معنا 😊", score: 10, feedback: "أنت شخص رائع! الترحيب بالآخرين يجعلهم سعداء!" },
      { text: "أبتسم له من بعيد 🙂", score: 5, feedback: "جيد! لكن لو تكلمته سيكون أسعد!" },
      { text: "لا أنتبه له 😐", score: 1, feedback: "تخيل لو كنت أنت الجديد... ألن تحب أن يرحب بك أحد؟" },
    ],
  },
  {
    question: "شعرت بالغضب الشديد من زميلك... ماذا تفعل؟",
    options: [
      { text: "أتنفس بعمق وأحاول أهدأ 🧘", score: 10, feedback: "ممتاز! التنفس العميق يساعدنا نتصرف بحكمة!" },
      { text: "أصرخ عليه بصوت عالي 😡", score: 1, feedback: "الصراخ يزيد المشاكل. التهدئة أولاً ثم نتكلم!" },
      { text: "أذهب لمكان هادئ لحد ما أهدأ 🚶", score: 8, feedback: "ذكي! الابتعاد مؤقتاً يساعدنا نفكر بوضوح!" },
    ],
  },
  {
    question: "أنت في الطابور وأحد تجاوزك... ماذا تفعل؟",
    options: [
      { text: "أقول له بأدب: من فضلك الدور هنا 🙋", score: 10, feedback: "أحسنت! احترام الدور مهم للجميع!" },
      { text: "أتجاوز أنا كمان 😏", score: 1, feedback: "لو الكل تجاوز ما حد يستفيد! الالتزام بالدور أفضل!" },
      { text: "أخبر المعلمة 👩‍🏫", score: 7, feedback: "جيد! لكن لو قدرت تحلها بنفسك بأدب يكون أحلى!" },
    ],
  },
  {
    question: "صديقك أعطاك جزء من أكله... ماذا تقول؟",
    options: [
      { text: "شكراً جزيلاً! أنت كريم جداً 🤲", score: 10, feedback: "الشكر والامتنان يقوّي صداقاتنا!" },
      { text: "آخذه بدون ما أقول شيء 🤷", score: 2, feedback: "كلمة شكراً بسيطة لكنها تعني الكثير!" },
      { text: "أعطيه شيء من أكلي بالمقابل 🔄", score: 8, feedback: "رد الجميل جميل! والشكر أيضاً مهم!" },
    ],
  },
  {
    question: "رأيت طفلاً صغيراً يبحث عن أمه وخائف... ماذا تفعل؟",
    options: [
      { text: "أمسك يده وأساعده يلاقي أمه 🤝", score: 10, feedback: "أنت بطل! مساعدة الصغار شيء نبيل!" },
      { text: "أخبر أقرب شخص كبير 📢", score: 8, feedback: "تصرف ذكي! الكبار يقدرون يساعدوه أسرع!" },
      { text: "أكمل طريقي 🚶", score: 1, feedback: "الأفضل نساعد المحتاجين ولا نتركهم!" },
    ],
  },
  {
    question: "كسرت شيء في البيت بالغلط... ماذا تفعل؟",
    options: [
      { text: "أخبر ماما أو بابا بالحقيقة 🤲", score: 10, feedback: "الصدق شجاعة! أهلك يقدّرون صراحتك!" },
      { text: "أخبّيه ولا أقول لأحد 🙈", score: 2, feedback: "إخفاء الحقيقة يسبب مشاكل أكبر. الصدق دائماً أفضل!" },
      { text: "أقول أخوي هو اللي كسره 😬", score: 1, feedback: "الكذب على الآخرين ظلم. الشجاع يقول الحقيقة!" },
    ],
  },
  {
    question: "بعد ما أكلت... ماذا تفعل بالمكان؟",
    options: [
      { text: "أنظف مكاني وأرمي الزبالة في سلة المهملات 🧹", score: 10, feedback: "ممتاز! النظافة من الإيمان!" },
      { text: "أترك الفوضى وأروح 😅", score: 1, feedback: "المكان النظيف يسعد الجميع! خلينا نحافظ عليه!" },
      { text: "أنظف بس لو طلبوا مني 🤷", score: 4, feedback: "النظافة مسؤوليتنا حتى بدون ما أحد يطلب!" },
    ],
  },
];

interface Props {
  onComplete: (score: number, stars: number, activityKey: string) => void;
  onBack: () => void;
}

export default function BehavioralActivity({ onComplete, onBack }: Props) {
  const [currentIndex, setCurrentIndex] = useState(0);
  const [selectedOption, setSelectedOption] = useState<number | null>(null);
  const [totalScore, setTotalScore] = useState(0);
  const [showFeedback, setShowFeedback] = useState(false);
  const [finished, setFinished] = useState(false);

  const scenario = SCENARIOS[currentIndex];

  const handleSelect = (idx: number) => {
    if (showFeedback) return;
    setSelectedOption(idx);
    setShowFeedback(true);
    setTotalScore(prev => prev + scenario.options[idx].score);
  };

  const handleNext = () => {
    if (currentIndex < SCENARIOS.length - 1) {
      setCurrentIndex(prev => prev + 1);
      setSelectedOption(null);
      setShowFeedback(false);
    } else {
      setFinished(true);
      const maxScore = SCENARIOS.length * 10;
      const pct = (totalScore / maxScore) * 100;
      const stars = pct >= 80 ? 3 : pct >= 50 ? 2 : 1;
      onComplete(totalScore, stars, "behavioral_scenarios");
    }
  };

  if (finished) {
    const maxScore = SCENARIOS.length * 10;
    const pct = (totalScore / maxScore) * 100;
    const stars = pct >= 80 ? 3 : pct >= 50 ? 2 : 1;
    return (
      <Card className="text-center border-2 border-yellow-300 bg-gradient-to-b from-yellow-50 to-white">
        <CardContent className="p-8 space-y-4">
          <div className="text-6xl">🎉</div>
          <h2 className="text-2xl font-bold text-foreground">أحسنت! أنهيت النشاط!</h2>
          <div className="flex justify-center gap-1">
            {[1, 2, 3].map(s => (
              <Star key={s} className={`h-10 w-10 ${s <= stars ? "text-yellow-400 fill-yellow-400" : "text-muted"}`} />
            ))}
          </div>
          <p className="text-lg text-muted-foreground">حصلت على {totalScore} من {maxScore} نقطة</p>
          <Button onClick={onBack} variant="outline" className="mt-4">
            <ArrowLeft className="h-4 w-4 ml-2" /> العودة للأنشطة
          </Button>
        </CardContent>
      </Card>
    );
  }

  return (
    <div className="space-y-4">
      <div className="flex items-center justify-between">
        <Button onClick={onBack} variant="ghost" size="sm">
          <ArrowLeft className="h-4 w-4 ml-1" /> رجوع
        </Button>
        <Badge variant="secondary">{currentIndex + 1} / {SCENARIOS.length}</Badge>
      </div>

      <Card className="border-2 border-primary/20 bg-gradient-to-br from-blue-50 to-purple-50">
        <CardHeader>
          <CardTitle className="text-xl text-center leading-relaxed">{scenario.question}</CardTitle>
        </CardHeader>
        <CardContent className="space-y-3">
          {scenario.options.map((opt, idx) => (
            <button
              key={idx}
              onClick={() => handleSelect(idx)}
              disabled={showFeedback}
              className={`w-full p-4 rounded-xl text-right text-lg transition-all border-2 ${
                selectedOption === idx
                  ? opt.score >= 8
                    ? "border-green-400 bg-green-50"
                    : "border-orange-400 bg-orange-50"
                  : "border-muted hover:border-primary/40 bg-card hover:bg-accent"
              } ${showFeedback && selectedOption !== idx ? "opacity-50" : ""}`}
            >
              <div className="flex items-center gap-3">
                {showFeedback && selectedOption === idx && (
                  <CheckCircle className={`h-5 w-5 shrink-0 ${opt.score >= 8 ? "text-green-500" : "text-orange-500"}`} />
                )}
                <span>{opt.text}</span>
              </div>
            </button>
          ))}

          {showFeedback && selectedOption !== null && (
            <div className="mt-4 p-4 rounded-xl bg-primary/5 border border-primary/20 text-center">
              <p className="text-foreground font-medium">{scenario.options[selectedOption].feedback}</p>
            </div>
          )}

          {showFeedback && (
            <Button onClick={handleNext} className="w-full mt-2" size="lg">
              {currentIndex < SCENARIOS.length - 1 ? "السؤال التالي ➡️" : "عرض النتيجة 🎉"}
            </Button>
          )}
        </CardContent>
      </Card>
    </div>
  );
}
